import styled, { css } from 'styled-components';

import { Button as BaseButton } from 'components';
import { resetButton, resetInputNumber, withDecoratedScroll } from 'styles/common';

const C = {
  primary: '#00e5ff',
  secondary: '#005f73',
  accent: '#ff6b35',
  positive: '#00e676',
  negative: '#ff1744',
  text: 'rgba(180, 220, 240, 0.85)',
  textDim: 'rgba(120, 160, 190, 0.6)',
  title: 'rgba(220, 245, 255, 0.95)',
  glow: 'rgba(0, 229, 255, 0.3)',
  glowStrong: 'rgba(0, 229, 255, 0.6)',
  border: 'rgba(0, 229, 255, 0.15)',
  borderStrong: 'rgba(0, 229, 255, 0.3)',
  bg: 'rgba(0, 20, 40, 0.4)',
  bgSolid: 'rgba(0, 15, 30, 0.7)',
  link: '#00e5ff',
  inputBg: 'rgba(0, 20, 40, 0.5)',
  btnHovered: 'rgba(0, 229, 255, 0.08)',
};

const FONT_MONO = "'Share Tech Mono', monospace";
const FONT_LABEL = "'Rajdhani', sans-serif";

/* ═══════════════════════════════════════════
   SECTION STYLES (Activity, CopiedWallets)
   ═══════════════════════════════════════════ */
export const ActivitySection = styled.div`
  position: relative;
  height: 100%;
  padding: 12px;
  display: flex;
  flex-direction: column;
  border-radius: 2px;
  background: ${C.bg};
  border: 1px solid ${C.border};
  backdrop-filter: blur(4px);
`;

export const SectionHeader = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 12px;
  margin-bottom: 10px;
`;

export const SectionHeaderLeft = styled.div`
  display: flex;
  align-items: center;
  gap: 6px;
`;

export const SectionDot = styled.div<{ color?: string }>`
  width: 6px;
  height: 6px;
  border-radius: 50%;
  background: ${(props) => props.color || C.accent};
  box-shadow: 0 0 6px ${(props) => props.color || C.accent};
  flex-shrink: 0;
`;

export const SectionTitle = styled.h3`
  margin: 0;
  font-family: ${FONT_LABEL};
  font-size: 14px;
  font-weight: 600;
  letter-spacing: 1px;
  text-transform: uppercase;
  color: ${C.title};
`;

/* ═══════════════════════════════════════════
   TABLE
   ═══════════════════════════════════════════ */
export const TableWrapper = styled.div`
  ${withDecoratedScroll}
  max-height: 400px;
  overflow-y: auto;
  overflow-x: auto;
  position: relative;

  &::-webkit-scrollbar {
    width: 3px;
    height: 3px;
  }
  &::-webkit-scrollbar-track {
    background: transparent;
  }
  &::-webkit-scrollbar-thumb {
    background: ${C.border};
    border-radius: 2px;
  }
`;

export const FullHeightTableWrapper = styled(TableWrapper)`
  flex: 1;
  min-height: 0;
  max-height: none;
`;

export const DesktopOnlyTableWrapper = styled(FullHeightTableWrapper)`
  @media (max-width: 600px) {
    display: none;
  }
`;

export const Table = styled.table<{ $isScrolled?: boolean }>`
  width: 100%;
  border-collapse: collapse;
  border-spacing: 0;

  tbody tr {
    background: transparent;
    border-bottom: 1px solid ${C.border};
    transition: background 0.1s;
  }

  tbody tr:hover {
    background: rgba(0, 229, 255, 0.04);
  }

  th,
  td {
    padding: 8px 10px;
    font-family: ${FONT_MONO};
    font-size: 13px;
  }

  th {
    font-family: ${FONT_LABEL};
    font-size: 11px;
    font-weight: 600;
    letter-spacing: 1px;
    text-transform: uppercase;
    color: ${C.textDim};
    white-space: nowrap;
    text-align: left;
    border-bottom: 1px solid ${C.borderStrong};
  }

  td {
    color: ${C.text};
    white-space: nowrap;
  }

  th:first-child,
  td:first-child {
    padding-left: 12px;
  }

  th:last-child,
  td:last-child {
    padding-right: 12px;
  }

  thead {
    position: sticky;
    top: 0;
    z-index: 2;
    background: ${C.bgSolid};
    backdrop-filter: blur(6px);
  }

  ${(props) =>
    props.$isScrolled &&
    css`
      thead {
        box-shadow: 0 1px 8px rgba(0, 229, 255, 0.1);
      }
    `}

  @media (max-width: 600px) {
    td {
      font-size: 12px;
    }
  }
`;

export const Tr = styled.tr``;

export const Cell = styled.div`
  min-height: 30px;
  display: inline-flex;
  align-items: center;
`;

export const MobileCards = styled.div`
  display: none;

  @media (max-width: 600px) {
    display: flex;
    flex: 1;
    min-height: 0;
    flex-direction: column;
    gap: 10px;
    overflow-y: auto;
    padding-right: 2px;
  }
`;

export const MobileCard = styled.div`
  border: 1px solid ${C.border};
  border-radius: 2px;
  background: rgba(0, 15, 30, 0.45);
  padding: 10px;
  display: flex;
  flex-direction: column;
  gap: 10px;
`;

export const MobileCardHeader = styled.div`
  display: flex;
  flex-direction: column;
  gap: 4px;
`;

export const MobileCardTitle = styled.div`
  font-family: ${FONT_LABEL};
  font-size: 13px;
  letter-spacing: 0.5px;
  color: ${C.title};
`;

export const MobileCardSubtitle = styled.div`
  font-family: ${FONT_MONO};
  font-size: 11px;
  color: ${C.textDim};
`;

export const MobileStatsGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(2, minmax(0, 1fr));
  gap: 8px 12px;
`;

export const MobileStat = styled.div`
  display: flex;
  flex-direction: column;
  gap: 2px;
`;

export const MobileStatLabel = styled.span`
  font-family: ${FONT_LABEL};
  font-size: 10px;
  letter-spacing: 1px;
  text-transform: uppercase;
  color: ${C.textDim};
`;

export const MobileStatValue = styled.div`
  min-height: 20px;
  display: flex;
  align-items: center;
  font-family: ${FONT_MONO};
  font-size: 12px;
  color: ${C.text};
`;

/* ═══════════════════════════════════════════
   ACTION BUTTON
   ═══════════════════════════════════════════ */
export const ActionButton = styled.button<{ $variant?: 'danger' | 'warning' | 'default' }>`
  ${resetButton}
  display: inline-flex;
  align-items: center;
  justify-content: center;
  padding: 4px 10px;
  height: 24px;
  border-radius: 2px;
  font-family: ${FONT_MONO};
  font-size: 11px;
  white-space: nowrap;
  cursor: pointer;
  transition: all 0.15s;
  background: transparent;

  ${(props) => {
    if (props.$variant === 'danger') {
      return css`
        color: ${C.negative};
        border: 1px solid rgba(255, 23, 68, 0.4);

        &:hover {
          border-color: ${C.negative};
          background: rgba(255, 23, 68, 0.1);
          box-shadow: 0 0 8px rgba(255, 23, 68, 0.2);
        }
      `;
    }
    if (props.$variant === 'warning') {
      return css`
        color: ${C.accent};
        border: 1px solid rgba(255, 107, 53, 0.4);

        &:hover {
          border-color: ${C.accent};
          background: rgba(255, 107, 53, 0.1);
          box-shadow: 0 0 8px rgba(255, 107, 53, 0.2);
        }
      `;
    }
    return css`
      color: ${C.primary};
      border: 1px solid ${C.border};

      &:hover {
        border-color: ${C.primary};
        background: ${C.btnHovered};
      }
    `;
  }}
`;

export const Address = styled.div`
  display: flex;
  align-items: center;
  gap: 6px;
`;

export const TokenCell = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
`;

export const EntityIcon = styled.img`
  width: 20px;
  height: 20px;
  flex-shrink: 0;
  object-fit: cover;
  border-radius: 50%;
  border: 1px solid ${C.border};
  background: rgba(0, 15, 30, 0.7);
`;

export const MarketIcon = styled(EntityIcon)`
  border-radius: 4px;
`;

export const Symbol = styled.span`
  font-family: ${FONT_MONO};
  font-size: 13px;
  color: ${C.title};
`;

export const AddressLink = styled.a`
  font-family: ${FONT_MONO};
  font-size: 13px;
  color: ${C.link};
  text-decoration: none;

  &:hover {
    text-shadow: 0 0 6px ${C.glow};
  }

  @media (max-width: 600px) {
    td {
      font-size: 12px;
    }
  }
`;

export const EmptyState = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  min-height: 160px;
  padding: 30px 20px;
  font-family: ${FONT_MONO};
  font-size: 13px;
  color: ${C.textDim};

  > div:first-child {
    font-size: 13px;
    margin-bottom: 8px;
    letter-spacing: 2px;
    text-transform: uppercase;
    color: ${C.textDim};
  }
`;

export const TxLink = styled.a`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  padding: 4px;
  font-family: ${FONT_MONO};
  font-size: 12px;
  color: ${C.textDim};
  text-decoration: none;

  &:hover {
    color: ${C.primary};
    text-shadow: 0 0 6px ${C.glow};
  }
`;

/* ═══════════════════════════════════════════
   TRADE TYPE BADGE
   ═══════════════════════════════════════════ */
export const TradeType = styled.span<{ type: 'BUY' | 'SELL' }>`
  display: inline-flex;
  align-items: center;
  padding: 2px 6px;
  border-radius: 2px;
  font-family: ${FONT_MONO};
  font-size: 10px;
  letter-spacing: 1px;
  text-transform: uppercase;

  ${(props) =>
    props.type === 'BUY'
      ? css`
          border: 1px solid rgba(0, 230, 118, 0.3);
          color: ${C.positive};
        `
      : css`
          border: 1px solid rgba(255, 23, 68, 0.3);
          color: ${C.negative};
        `}
`;

/* ═══════════════════════════════════════════
   COLUMNS BUTTON
   ═══════════════════════════════════════════ */
export const ColumnsButton = styled.button`
  ${resetButton}
  display: flex;
  align-items: center;
  gap: 6px;
  padding: 4px 8px;
  border-radius: 2px;
  font-family: ${FONT_MONO};
  font-size: 11px;
  color: ${C.textDim};
  border: 1px solid ${C.border};
  transition: all 0.15s;
  cursor: pointer;

  &:hover {
    border-color: ${C.borderStrong};
    color: ${C.text};
  }
`;

export const ColumnsIcon = styled.div`
  width: 12px;
  height: 12px;
  background:
    radial-gradient(circle, currentColor 1.2px, transparent 1.2px) 0 0 / 4px 4px,
    radial-gradient(circle, currentColor 1.2px, transparent 1.2px) 4px 0 / 4px 4px,
    radial-gradient(circle, currentColor 1.2px, transparent 1.2px) 8px 0 / 4px 4px,
    radial-gradient(circle, currentColor 1.2px, transparent 1.2px) 0 4px / 4px 4px,
    radial-gradient(circle, currentColor 1.2px, transparent 1.2px) 4px 4px / 4px 4px,
    radial-gradient(circle, currentColor 1.2px, transparent 1.2px) 8px 4px / 4px 4px;
  background-repeat: no-repeat;
`;

export const ChevronIcon = styled.div`
  width: 0;
  height: 0;
  border-left: 3px solid transparent;
  border-right: 3px solid transparent;
  border-top: 4px solid currentColor;
  margin-left: 2px;
`;

/* ═══════════════════════════════════════════
   COLUMNS MODAL
   ═══════════════════════════════════════════ */
export const ColumnsModalContent = styled.div`
  width: 100vw;
  max-width: 280px;
  font-family: ${FONT_LABEL};
`;

export const ModalTitle = styled.div`
  margin-bottom: 10px;
  font-family: ${FONT_LABEL};
  font-size: 16px;
  font-weight: 600;
  letter-spacing: 1px;
  text-transform: uppercase;
  color: ${C.title};
`;

export const ColumnsList = styled.div`
  display: flex;
  flex-direction: column;
`;

export const ColumnItem = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 12px;
  padding: 8px 0;
  border-bottom: 1px solid ${C.border};

  &:last-child {
    border-bottom: none;
  }
`;

export const ColumnName = styled.span`
  font-family: ${FONT_LABEL};
  font-size: 13px;
  font-weight: 500;
  color: ${C.title};
`;

export const ToggleWrapper = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
  flex-shrink: 0;
  margin-left: auto;
`;

export const LoadMoreButton = styled.div`
  width: 100%;
  padding: 10px;
  font-family: ${FONT_MONO};
  font-size: 12px;
  text-align: center;
  color: ${C.primary};
  cursor: pointer;

  &:hover {
    text-shadow: 0 0 6px ${C.glow};
  }
`;

/* ═══════════════════════════════════════════
   SETTINGS
   ═══════════════════════════════════════════ */
export const SettingsContent = styled.div`
  margin: 12px auto;
  max-width: 460px;
  display: flex;
  flex-direction: column;
  gap: 14px;
  padding: 20px;
  background: ${C.bg};
  border: 1px solid ${C.border};
  border-radius: 2px;
  backdrop-filter: blur(4px);
`;

export const SettingsLabel = styled.label`
  font-family: ${FONT_LABEL};
  font-size: 13px;
  color: ${C.text};
  display: flex;
  flex-direction: column;
  gap: 4px;

  b {
    color: ${C.title};
  }
`;

export const SettingsLabelRow = styled.span`
  display: inline-flex;
  align-items: center;
  gap: 6px;
`;

export const SettingsInput = styled.input<{ $isError?: boolean }>`
  ${resetInputNumber}
  padding: 8px 10px;
  border: 1px solid ${C.border};
  border-radius: 2px;
  background: ${C.inputBg};
  color: ${C.title};
  font-family: ${FONT_MONO};
  font-size: 13px;
  transition: border-color 0.15s;

  &:focus {
    outline: none;
    border-color: ${C.primary};
    box-shadow: 0 0 6px ${C.glow};
  }

  ${(props) =>
    props.$isError &&
    css`
      border-color: ${C.negative};
    `}
`;

export const SettingsSelect = styled.select<{ $isError?: boolean }>`
  padding: 8px 10px;
  border: 1px solid ${C.border};
  border-radius: 2px;
  background: ${C.inputBg};
  color: ${C.title};
  font-family: ${FONT_MONO};
  font-size: 13px;
  transition: border-color 0.15s;

  &:focus {
    outline: none;
    border-color: ${C.primary};
    box-shadow: 0 0 6px ${C.glow};
  }

  ${(props) =>
    props.$isError &&
    css`
      border-color: ${C.negative};
    `}
`;

export const SettingsTextarea = styled.textarea<{ $isError?: boolean }>`
  min-height: 120px;
  resize: vertical;
  padding: 8px 10px;
  border: 1px solid ${C.border};
  border-radius: 2px;
  background: ${C.inputBg};
  color: ${C.title};
  font-family: ${FONT_MONO};
  font-size: 13px;
  transition: border-color 0.15s;

  &:focus {
    outline: none;
    border-color: ${C.primary};
    box-shadow: 0 0 6px ${C.glow};
  }

  ${(props) =>
    props.$isError &&
    css`
      border-color: ${C.negative};
    `}
`;

export const SettingsGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(2, minmax(0, 1fr));
  gap: 14px;

  @media (max-width: 600px) {
    grid-template-columns: 1fr;
  }
`;

export const SettingsSectionTitle = styled.div`
  margin-top: 6px;
  font-family: ${FONT_LABEL};
  font-size: 12px;
  letter-spacing: 1px;
  text-transform: uppercase;
  color: ${C.textDim};
`;

export const SettingsHint = styled.span<{ $isError?: boolean }>`
  font-family: ${FONT_MONO};
  font-size: 11px;
  color: ${(props) => (props.$isError ? C.negative : C.textDim)};
  line-height: 1.4;
`;

export const SettingsTooltip = styled.span`
  position: relative;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 14px;
  height: 14px;
  border: 1px solid ${C.borderStrong};
  border-radius: 50%;
  color: ${C.primary};
  font-family: ${FONT_MONO};
  font-size: 10px;
  line-height: 1;
  cursor: help;

  &::before {
    content: '?';
  }

  &::after {
    content: attr(data-tooltip);
    position: absolute;
    left: 50%;
    top: calc(100% + 8px);
    transform: translateX(-50%);
    width: 220px;
    padding: 8px 10px;
    border: 1px solid ${C.borderStrong};
    border-radius: 2px;
    background: ${C.bgSolid};
    color: ${C.text};
    font-family: ${FONT_MONO};
    font-size: 11px;
    line-height: 1.4;
    white-space: normal;
    opacity: 0;
    pointer-events: none;
    box-shadow: 0 0 12px rgba(0, 229, 255, 0.12);
    transition: opacity 0.15s ease;
    z-index: 4;
  }

  &:hover::after,
  &:focus-visible::after {
    opacity: 1;
  }
`;

export const SettingsMinMax = styled.div`
  display: flex;
  align-items: baseline;
  gap: 4px;
  font-family: ${FONT_MONO};
  font-size: 12px;
  color: ${C.link};

  button {
    ${resetButton}
    margin: 0 4px;
    color: ${C.link};

    &:hover {
      text-shadow: 0 0 6px ${C.glow};
    }
  }
`;

export const SettingsButtons = styled.div`
  margin-top: 16px;
  width: 100%;
  display: flex;
  gap: 8px;

  > * {
    white-space: nowrap;
  }
`;

/* ═══════════════════════════════════════════
   ADD WALLET BAR
   ═══════════════════════════════════════════ */
export const AddWalletBar = styled.div`
  display: flex;
  align-items: center;
  margin-bottom: 8px;
  padding: 6px 10px;
  border: 1px solid ${C.border};
  border-radius: 2px;
  background: ${C.bg};
  gap: 10px;

  @media (max-width: 600px) {
    flex-direction: column;
    align-items: stretch;
  }
`;

export const AddWalletLeft = styled.div`
  display: flex;
  align-items: center;
  gap: 10px;
  flex-wrap: wrap;
`;

export const AddWalletRight = styled.div`
  margin-left: auto;
  display: flex;
  align-items: center;
  gap: 8px;
  flex: 1;
  width: 100%;

  @media (max-width: 600px) {
    margin-left: 0;
    max-width: none;
  }
`;

export const AddWalletInput = styled.input`
  flex: 1;
  width: 100%;
  padding: 6px 10px;
  border: 1px solid ${C.border};
  border-radius: 2px;
  background: ${C.inputBg};
  color: ${C.title};
  font-family: ${FONT_MONO};
  font-size: 12px;
  min-width: 200px;

  &::placeholder {
    color: ${C.textDim};
  }

  &:focus {
    outline: none;
    border-color: ${C.primary};
    box-shadow: 0 0 6px ${C.glow};
  }
`;

export const SelectedCount = styled.span`
  font-family: ${FONT_MONO};
  font-size: 11px;
  color: ${C.title};
`;

export const SelectAllText = styled.span`
  font-family: ${FONT_MONO};
  font-size: 11px;
  color: ${C.link};
  cursor: pointer;

  &:hover {
    text-shadow: 0 0 6px ${C.glow};
  }
`;

export const BulkActionButton = styled.button<{ $variant: 'danger' | 'warning' | 'success' }>`
  ${resetButton}
  height: 26px;
  display: flex;
  align-items: center;
  gap: 5px;
  padding: 0 10px;
  border-radius: 2px;
  font-family: ${FONT_MONO};
  font-size: 11px;
  cursor: pointer;
  transition: all 0.15s;
  background: transparent;
  border: 1px solid ${C.border};
  color: ${C.text};

  &:hover {
    border-color: ${C.borderStrong};
    background: ${C.btnHovered};
  }

  &:disabled {
    pointer-events: none;
    opacity: 0.4;
  }
`;

export const SelectableTr = styled(Tr)<{ $isSelected: boolean; $isPaused: boolean }>`
  ${(props) =>
    props.$isSelected &&
    css`
      background: rgba(0, 229, 255, 0.06) !important;
    `}

  ${(props) =>
    props.$isPaused &&
    css`
      opacity: 0.5;
    `}
`;

export const PausedBadge = styled.span`
  margin-left: 6px;
  padding: 1px 6px;
  border-radius: 2px;
  font-family: ${FONT_MONO};
  font-size: 9px;
  letter-spacing: 1px;
  text-transform: uppercase;
  border: 1px solid rgba(255, 107, 53, 0.3);
  color: ${C.accent};
`;

export const TokensBadge = styled.span`
  padding: 1px 6px;
  border-radius: 2px;
  font-family: ${FONT_MONO};
  font-size: 11px;
  border: 1px solid rgba(0, 230, 118, 0.3);
  color: ${C.positive};
`;

export const PositionsHeader = styled.div`
  margin: 0 0 8px;
  display: flex;
  flex-wrap: wrap;
  justify-content: flex-end;
  align-items: center;
  gap: 10px;
  padding: 6px 10px;
  border: 1px solid ${C.border};
  border-radius: 2px;
  background: ${C.bg};
  font-family: ${FONT_MONO};
  font-size: 12px;
  color: ${C.text};
`;

export const PositionsButtons = styled.div`
  display: flex;
  align-items: center;
  justify-content: flex-end;
  gap: 6px;
`;

// export const FilterButton = styled.button<{ isActive: boolean }>`
//   ${resetButton}
//   display: inline-flex;
//   padding: 2px;
//   margin: -2px;
//   font-family: ${FONT_MONO};
//   font-size: 12px;
//   color: ${C.textDim};

//   &:hover {
//     color: ${C.primary};
//     text-shadow: 0 0 4px ${C.glow};
//   }

//   ${(props) =>
//     props.isActive &&
//     css`
//       color: ${C.primary} !important;
//       text-shadow: 0 0 6px ${C.glow};
//     `}
// `;

export const FilterButton = styled.button<{ $isActive: boolean }>`
  ${resetButton};
  display: inline-flex;
  padding: 2px;
  margin: -2px;
  position: relative;

  svg {
    width: 14px;

    path {
      fill: ${C.textDim};
    }
  }

  &:hover svg path {
    fill: ${C.text};
  }

  ${(props) =>
    props.$isActive &&
    css`
      svg path {
        fill: ${C.glow} !important;
      }
    `}
`;

export const RankBadge = styled.span<{ $rank: number }>`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 20px;
  height: 20px;
  border-radius: 2px;
  font-family: ${FONT_MONO};
  font-size: 11px;

  ${(props) => {
    if (props.$rank <= 3) {
      return css`
        color: ${C.accent};
        border: 1px solid rgba(255, 107, 53, 0.4);
        text-shadow: 0 0 4px rgba(255, 107, 53, 0.5);
      `;
    }
    return css`
      color: ${C.textDim};
      border: 1px solid ${C.border};
    `;
  }}
`;

/* ═══════════════════════════════════════════
   BLUE BUTTON → CYAN OUTLINE
   ═══════════════════════════════════════════ */
export const BlueButton = styled(BaseButton)`
  background: transparent;
  color: ${C.primary};
  border: 1px solid ${C.primary};
  border-radius: 2px;
  padding: 4px 14px;
  min-height: 30px;
  font-family: ${FONT_MONO};
  font-size: 12px;
  letter-spacing: 1px;
  text-transform: uppercase;

  &:hover:not(:disabled) {
    background: rgba(0, 229, 255, 0.1);
    box-shadow: 0 0 10px ${C.glow};
    text-shadow: 0 0 6px ${C.glow};
  }

  &:disabled {
    opacity: 0.4;
    cursor: not-allowed;
    pointer-events: none;
  }
`;

export const FindButton = styled.a`
  display: inline-flex;
  align-items: center;
  gap: 5px;
  background: transparent;
  color: ${C.primary};
  border: 1px solid ${C.primary};
  border-radius: 2px;
  padding: 4px 14px;
  min-height: 30px;
  font-family: ${FONT_MONO};
  font-size: 12px;
  letter-spacing: 1px;
  text-transform: uppercase;
  text-decoration: none;
  white-space: nowrap;
  cursor: pointer;
  transition: all 0.2s ease;

  &:hover {
    background: rgba(0, 229, 255, 0.1);
    box-shadow: 0 0 10px ${C.glow};
    text-shadow: 0 0 6px ${C.glow};
  }

  &::before {
    content: '\\1F50D';
    font-size: 11px;
  }
`;

export const SettingsBlueButton = styled(BlueButton)<{ isFullWidth?: boolean }>`
  ${(props) =>
    props.isFullWidth &&
    css`
      width: 100%;
    `}
`;

/* ═══════════════════════════════════════════
   TOGGLE SWITCH
   ═══════════════════════════════════════════ */
export const ToggleSwitch = styled.label`
  position: relative;
  display: inline-block;
  width: 36px;
  height: 20px;
  cursor: pointer;

  input {
    opacity: 0;
    width: 0;
    height: 0;

    &:checked + span {
      background: ${C.positive};
      border-color: ${C.positive};
      box-shadow: 0 0 6px rgba(0, 230, 118, 0.4);
    }

    &:checked + span::before {
      transform: translateX(16px);
    }
  }

  span {
    position: absolute;
    inset: 0;
    background: rgba(0, 229, 255, 0.15);
    border: 1px solid ${C.border};
    border-radius: 2px;
    transition: 0.2s;

    &::before {
      content: '';
      position: absolute;
      width: 14px;
      height: 14px;
      left: 2px;
      bottom: 2px;
      background: ${C.title};
      border-radius: 2px;
      transition: 0.2s;
    }
  }
`;

/* ═══════════════════════════════════════════
   RESET BUTTON (Settings)
   ═══════════════════════════════════════════ */
export const ResetButton = styled.button`
  ${resetButton}
  width: 100%;
  padding: 8px 16px;
  font-family: ${FONT_MONO};
  font-size: 12px;
  letter-spacing: 1px;
  text-transform: uppercase;
  color: ${C.textDim};
  border: 1px solid ${C.border};
  border-radius: 2px;
  cursor: pointer;
  transition: all 0.2s;

  &:hover:not(:disabled) {
    color: ${C.text};
    border-color: ${C.borderStrong};
    background: ${C.btnHovered};
  }

  &:disabled {
    opacity: 0.3;
    cursor: not-allowed;
  }
`;
